import { cn } from "@/lib/utils";
import {
  ClusterType,
  DynamicSupplyHardware,
  DynamicSupplyHardwareFiltersResponse
} from "@/types";
import React from "react";
import { HardwareCard } from "../../HardwareCard";
import { HardwareGridSkeleton } from "./HardwareGridSkeleton";
import { HardwareGridEmpty } from "./HardwareGridEmpty";
import { VerticalFlowAnalytics } from "../../analytics";

type HardwareGridProps = {
  items: DynamicSupplyHardware[];
  selectedId: string | null;
  onSelect: (id: string) => void;
  /** Double-click on a card selects that row and invokes this with the hardware (optional). */
  onAdvanceProcessorStep?: (hardware: DynamicSupplyHardware) => void;
  onShowDetails: (item: DynamicSupplyHardware) => void;
  clusterType: ClusterType;
  isLoading: boolean;
  showSummaryPanel: boolean;
  searchValue: string;
  selectedFilters: Partial<DynamicSupplyHardwareFiltersResponse> | null;
  analytics?: Pick<
    VerticalFlowAnalytics,
    "trackRequestDevicesClicked" | "trackRequestDevicesSubmitted"
  >;
  onResetFilters: () => void;
};

const DESCRIPTION_MAP = {
  [ClusterType.VMAAS]: "Request virtual machine support for selected device(s).",
  [ClusterType.CONTAINER]: "Request container support for selected device(s).",
  [ClusterType.KUBERNETES]: "Request Kubernetes support for selected device(s)."
};

export const HIDDEN_HARDWARE_FIELDS = {
  [ClusterType.CONTAINER]: [
    "cpuCores",
    "deviceMemory",
    "storage",
    "gpuMemory",
    "interconnect",
    "nvlink",
    "vCPU"
  ],
  [ClusterType.KUBERNETES]: []
};

export const HardwareGrid: React.FC<HardwareGridProps> = ({
  searchValue,
  items,
  selectedId,
  onSelect,
  onAdvanceProcessorStep,
  onShowDetails,
  clusterType,
  showSummaryPanel,
  isLoading,
  selectedFilters,
  analytics,
  onResetFilters
}) => {
  const usesMainContentScroll =
    clusterType === ClusterType.VMAAS || clusterType === ClusterType.CONTAINER;

  const gridColumnClasses = usesMainContentScroll
    ? "max-lg:pr-0 md:grid-cols-2 xl:grid-cols-3 3xl:grid-cols-4"
    : cn(
        "custom-scrollbar overflow-y-auto max-lg:h-full",
        showSummaryPanel
          ? "lg:h-[500px] xl:grid-cols-2"
          : "max-lg:pr-0 md:grid-cols-2 lg:h-[calc(100vh-384px)] xl:grid-cols-3 3xl:grid-cols-4"
      );

  if (isLoading) {
    return (
      <HardwareGridSkeleton
        usesMainContentScroll={usesMainContentScroll}
        showSummaryPanel={showSummaryPanel}
      />
    );
  }

  if (items.length === 0) {
    return (
      <HardwareGridEmpty
        clusterType={clusterType}
        searchValue={searchValue}
        selectedFilters={selectedFilters}
        description={DESCRIPTION_MAP[clusterType as keyof typeof DESCRIPTION_MAP]}
        onResetFilters={onResetFilters}
      />
    );
  }
  return (
    <div
      id="hardware-grid-container"
      className={cn(
        "grid auto-rows-max grid-cols-1 content-start items-start gap-4 pt-2 max-lg:w-full",
        gridColumnClasses
      )}
    >
      {items.map((item) => (
        <HardwareCard
          description={DESCRIPTION_MAP[clusterType as keyof typeof DESCRIPTION_MAP]}
          key={item.hardwareId}
          item={item}
          isSelected={selectedId === item.hardwareId}
          onSelect={onSelect}
          onAdvanceProcessorStep={onAdvanceProcessorStep}
          onShowDetails={onShowDetails}
          analytics={analytics}
          hiddenHardwareFields={
            HIDDEN_HARDWARE_FIELDS[clusterType as keyof typeof HIDDEN_HARDWARE_FIELDS]
          }
          clusterType={clusterType}
        />
      ))}
    </div>
  );
};
